import { useCallback, useEffect } from 'react';
import { isSameAddress } from '@dynamic-labs/wallet-connector-core';
import { isWalletLinked } from '../../../functions/isWalletLinked/isWalletLinked.js';

const useSyncPrimaryWallet = ({ computeConnectedStateForWallets, handleLogOut, multiWalletWidgetState, primaryWallet, secondaryWallets, setMultiWalletWidgetState, setPrimaryWallet, setShowWidgetStatePopup, showWidgetStatePopup, canSync = true, }) => {
    const handlePrimaryWalletNotActive = useCallback((connectedAddress) => {
        if (!primaryWallet)
            return;
        const connectedWalletIsLinked = isWalletLinked(connectedAddress, [
            primaryWallet,
            ...secondaryWallets,
        ]);
        if (!connectedWalletIsLinked) {
            // MM returns all connected wallets now, so we need to check if the active account
            // is not in the same wallet as the primary wallet before automatically switching
            // to a connected secondary wallet as it was causing an infinite loop
            const connectedSecondaryWallet = secondaryWallets.find((wallet) => wallet.connected && primaryWallet.connector !== wallet.connector);
            if (!connectedSecondaryWallet) {
                setMultiWalletWidgetState('awaiting_account_switch', 'primary_not_connected');
                return;
            }
            setPrimaryWallet(connectedSecondaryWallet.id);
            computeConnectedStateForWallets();
            return;
        }
        if (showWidgetStatePopup) {
            // user updates wallet address in the widget
            setShowWidgetStatePopup(false);
            setMultiWalletWidgetState('awaiting_account_switch', 'switching_primary');
            return;
        }
        // !showWidgetStatePopup - user comes back to site with different wallet address
        const connectedWallet = secondaryWallets.find((wallet) => wallet.connected &&
            isSameAddress(wallet.address, connectedAddress, wallet.chain));
        if (connectedWallet) {
            setPrimaryWallet(connectedWallet.id);
            computeConnectedStateForWallets();
            return;
        }
        setMultiWalletWidgetState('awaiting_account_switch', 'primary_not_connected');
    }, [
        primaryWallet,
        secondaryWallets,
        showWidgetStatePopup,
        setShowWidgetStatePopup,
        setMultiWalletWidgetState,
        setPrimaryWallet,
        computeConnectedStateForWallets,
    ]);
    useEffect(() => {
        if (!canSync)
            return;
        if (!primaryWallet || multiWalletWidgetState !== 'idle')
            return;
        if (primaryWallet.connector.connectedChain === 'FLOW')
            return;
        if (primaryWallet.connector.connectedChain === 'COSMOS')
            return;
        primaryWallet.connector
            .getConnectedAccounts()
            .then(([connectedAddress]) => {
            if (isSameAddress(connectedAddress, primaryWallet.address, primaryWallet.chain)) {
                return;
            }
            handlePrimaryWalletNotActive(connectedAddress);
        })
            .catch(() => {
            const connectedSecondaryWallet = secondaryWallets.find((wallet) => wallet.connected);
            if (connectedSecondaryWallet) {
                setPrimaryWallet(connectedSecondaryWallet.id);
                computeConnectedStateForWallets();
                return;
            }
            handleLogOut();
        });
    }, [
        primaryWallet,
        secondaryWallets,
        multiWalletWidgetState,
        setMultiWalletWidgetState,
        computeConnectedStateForWallets,
        handlePrimaryWalletNotActive,
        handleLogOut,
        setPrimaryWallet,
        canSync,
    ]);
};

export { useSyncPrimaryWallet };
